import { Icon, Link, Typography } from '@globalmed/atlas-ui';
import cn from 'classnames';
import { memo, useId, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './UploadFile.module.css';
import { useUploadFile } from '@/hooks/useUploadFile';
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from '@/utils/constants';

type TProps = {
  id?: string;
  accept?: string;
  maxFileSize?: number;
  disabled?: boolean;
  multiple?: boolean;
  setFiles?: (file: File[]) => void;
  className?: string;
  text?: string;
  showSettings?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  /**
   * Accessible name for the "choose file" link, which is the element that
   * actually receives keyboard focus. Include the field name here (e.g.
   * "Upload Photo ID") so screen readers announce which upload it is.
   */
  chooseFileAriaLabel?: string;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
};

export const UploadFile =
  /**
   * File upload component with drag-and-drop support and file selection via dialog.
   * Uses the `useUploadFile` hook to manage upload state, errors, and drag-and-drop events.
   * @param props - Component props
   * @param props.id - HTML `id` attribute for the file input element.
   * @param props.accept - Comma-separated string of accepted MIME types or file extensions.
   * @param props.maxFileSize - Maximum allowed file size in bytes.
   * @param props.disabled - Whether file input and drag-and-drop are disabled.
   * @param props.multiple - Whether selecting/dropping multiple files is allowed. Defaults to true. When false, the input loses the `multiple` attribute and selecting more than one file produces an error.
   * @param props.setFiles - Callback invoked with validated files.
   * @param props.className - Additional CSS class for the container.
   * @param props['aria-label'] - HTML `aria-label` attribute for the file input element.
   * @param props['aria-describedby'] - HTML `aria-describedby` attribute for the file input element.
   * @param props.chooseFileAriaLabel - Accessible name for the focusable "choose file" link; include the field name so screen readers announce which upload it is.
   * @param props.onClick - Click handler attached to the underlying file input.
   * @returns JSX.Element representing a file upload area with drag-and-drop and a file selection button
   */
  memo((props: TProps) => {
    const { t } = useTranslation();
    const {
      id,
      accept = ALLOWED_FILE_TYPES,
      disabled = false,
      multiple = true,
      className,
      text,
      showSettings = false,
      maxFileSize = MAX_FILE_SIZE,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedby,
      chooseFileAriaLabel,
      onClick,
    } = props;

    const {
      isDragging,
      error,
      ref,
      handleFileChange,
      handleDragOver,
      handleDragLeave,
      handleDrop,
    } = useUploadFile(props);

    const generatedInputId = useId();
    const inputId = id ?? generatedInputId;
    const settingsId = useId();
    const errorId = useId();
    // Link the input to the settings hint, the validation error and any
    // externally provided description so screen readers announce them all.
    const describedBy =
      [
        ariaDescribedby,
        showSettings ? settingsId : null,
        error ? errorId : null,
      ]
        .filter(Boolean)
        .join(' ') || undefined;

    return (
      <div
        className={cn(
          styles.container,
          { [styles.dragging]: isDragging },
          className,
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={ref}
          id={inputId}
          className={styles.input}
          type="file"
          accept={accept}
          disabled={disabled}
          multiple={multiple}
          data-testid="upload-input"
          aria-label={
            ariaLabel ?? chooseFileAriaLabel ?? text ?? t('upload-files')
          }
          aria-describedby={describedBy}
          onChange={handleFileChange}
          onClick={onClick}
        />

        <div className={styles.description}>
          <Icon
            name={'Upload'}
            size="20px"
            color="var(--colors-gray500)"
            decorative
          />

          <Typography sx={{ marginTop: '16px' }} fontWeight={600}>
            {text ?? t('upload-files')}
          </Typography>

          {showSettings && (
            <Typography id={settingsId}>
              {t('upload-settings', {
                fileTypes: accept,
                size: maxFileSize / 1024 / 1024,
              })}
            </Typography>
          )}

          <Link
            text={multiple ? t('choose-files') : t('choose-file')}
            size={'sm'}
            disabled={disabled}
            sx={{ fontWeight: 600 }}
            aria-label={chooseFileAriaLabel}
            aria-controls={inputId}
            aria-describedby={describedBy}
            onClick={() => ref.current?.click()}
          />
        </div>

        {error && (
          <p id={errorId} className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  });

UploadFile.displayName = 'UploadFile';
